<template>
  <v-dialog v-model="dialog" max-width="500" persistent>
    <v-card>
      <v-card-title class="subtitle-1">
        <v-icon left small color="primary">{{ isEdit ? 'mdi-key-change' : 'mdi-key-plus' }}</v-icon>
        {{ isEdit ? 'Edit API Key' : 'Create API Key' }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" @submit.prevent="submit">
          <!-- Name -->
          <v-text-field
            v-model="form.name"
            label="Name"
            :rules="[formRules.required, formRules.lengthMax(200)]"
            outlined
            dense
            :disabled="isEdit"
            placeholder="e.g., Production Integration"
            hint="A descriptive name for this key"
            persistent-hint
            class="mb-3" />

          <!-- Permission -->
          <v-select
            v-model="form.permission"
            :items="permissionOptions"
            label="Permission"
            :rules="[formRules.required]"
            outlined
            dense
            class="mb-3">
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">{{ item.description }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ item }">
              {{ item.text }}
            </template>
          </v-select>

          <!-- Expiration Date -->
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formattedExpiration"
                label="Expiration Date (optional)"
                prepend-inner-icon="mdi-calendar"
                outlined
                dense
                readonly
                clearable
                v-bind="attrs"
                v-on="on"
                @click:clear="form.expiresDate = null"
                hint="Leave empty for no expiration"
                persistent-hint />
            </template>
            <v-date-picker
              v-model="form.expiresDate"
              :min="minExpirationDate"
              @input="dateMenu = false"
              color="primary" />
          </v-menu>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text small @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          small
          :loading="loading"
          :disabled="!valid"
          @click="submit">
          {{ isEdit ? 'Save Changes' : 'Create Key' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import formRules from '@Share/config/form-rules';
import apiKeysService from '@/features/Company/service/api-keys.service';
import { error } from '@/utils/debugLogger';
import dayjs from 'dayjs';

export default {
  name: 'ApiKeyDialog',
  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      dateMenu: false,
      formRules,
      isEdit: false,
      editingId: null,
      form: {
        name: '',
        permission: 'read',
        expiresDate: null,
      },
      permissionOptions: [
        { value: 'read', text: 'Read', description: 'Can only read data (GET requests)' },
        { value: 'write', text: 'Write', description: 'Can read and modify data (all requests)' },
      ],
    };
  },
  computed: {
    formattedExpiration() {
      return this.form.expiresDate ? dayjs(this.form.expiresDate).format('YYYY-MM-DD') : '';
    },
    minExpirationDate() {
      return dayjs().add(1, 'day').format('YYYY-MM-DD');
    },
  },
  methods: {
    /**
     * Open the dialog for creating or editing an API key
     * @param {Object} [item] - Existing key to edit (omit for create mode)
     */
    open(item = null) {
      this.$refs.form?.resetValidation();
      if (item) {
        // Edit mode
        this.isEdit = true;
        this.editingId = item.id;
        this.form = {
          name: item.name,
          permission: item.permission,
          expiresDate: item.expiresDate ? dayjs(item.expiresDate).format('YYYY-MM-DD') : null,
        };
      } else {
        // Create mode
        this.isEdit = false;
        this.editingId = null;
        this.form = {
          name: '',
          permission: 'read',
          expiresDate: null,
        };
      }
      this.dialog = true;
    },

    close() {
      this.dialog = false;
      this.isEdit = false;
      this.editingId = null;
      this.form = { name: '', permission: 'read', expiresDate: null };
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        if (this.isEdit) {
          const res = await apiKeysService.update(this.editingId, {
            permission: this.form.permission,
            expiresDate: this.form.expiresDate || null,
          });
          this.$emit('updated', res.data);
          this.$dialog.notify.success('API key updated', { position: 'top-right', timeout: 2000 });
        } else {
          const res = await apiKeysService.create({
            name: this.form.name,
            permission: this.form.permission,
            expiresDate: this.form.expiresDate || null,
          });
          this.$emit('created', res.data);
          this.$dialog.notify.success('API key created', { position: 'top-right', timeout: 2000 });
        }
        this.close();
      } catch (err) {
        error('Failed to save API key:', err);
        this.$dialog.notify.error(err.response?.data?.error || 'Failed to save API key');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
